<template>
  <zg-module>
    <div
      :class="['zg-how-we-helped', {
        'zg-how-we-helped--with-image': image
      }]"
    >
      <div
        v-if="image || illustration"
        class="image"
      >
        <zg-image
          v-if="image"
          :src="image.src"
          :title="image.title"
          :alt="image.alt"
          :sources="image.sources"
          lazy-load
        />

        <zg-illustration
          v-else-if="illustration"
          :illustration="illustration"
        />
      </div>

      <div class="content">
        <h3>
          {{ title }}
        </h3>

        <p>
          {{ subtitle }}
        </p>

        <div class="comparison">
          <div class="comparison__labels">
            <div class="values">
              <div
                v-for="(item, index) in before.priceList"
                :key="index"
              >
                <span>{{ item.label }}</span>
              </div>
            </div>
          </div>

          <div class="comparison__before">
            <h4>{{ before.title }}</h4>

            <div class="values">
              <div
                v-for="(item, index) in before.priceList"
                :key="index"
              >
                <span>{{ item.label }}</span>
                <span>{{ item.value }}</span>
              </div>
            </div>
          </div>

          <div class="comparison__after">
            <h4>{{ after.title }}</h4>

            <div class="values">
              <div
                v-for="(item, index) in after.priceList"
                :key="index"
              >
                <span>&nbsp;</span>
                <span>{{ item.value }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="savings">
          <span>{{ savings.label }}</span>
          <span>{{ savings.value }}
            <template v-if="disclaimer">*</template>
          </span>
        </div>

        <zg-button-link
          v-if="button && button.link && button.label"
          :href="button.link"
          :text="button.label"
          :button-props="{
            icons: { right: 'arrow-forward' },
            fullWidthMobile: true,
            size: 'xlarge'
          }"
          class="button"
          @click.native="$emit('buttonClick')"
        />

        <div
          v-if="disclaimer"
          class="disclaimer"
        >
          <span>*</span>
          <span>{{ disclaimer }}</span>
        </div>
      </div>
    </div>
  </zg-module>
</template>

<script>
  import ZgModule from '../sections/ZgModule/ZgModule.vue'
  import ZgButtonLink from '../ZgButtonLink/ZgButtonLink'
  import ZgIllustration from '../ZgIllustration/ZgIllustration'
  import ZgImage from '../ZgImage/ZgImage'

  export default {
    name: 'ZgHowWeHelped',
    components: {
      ZgModule,
      ZgIllustration,
      ZgButtonLink,
      ZgImage
    },
    props: {
      title: {
        type: String,
        required: true
      },
      subtitle: {
        type: String,
        required: true
      },
      image: {
        type: Object,
        default: null
      },
      illustration: {
        type: String,
        default: null
      },
      before: {
        type: Object,
        required: true,
        validator: before => before.title &&
          Array.isArray(before.priceList) &&
          before.priceList.length &&
          before.priceList.every(item => item.label && item.value)
      },
      after: {
        type: Object,
        required: true,
        validator: after => after.title &&
          Array.isArray(after.priceList) &&
          after.priceList.length &&
          after.priceList.every(item => item.label && item.value)
      },
      savings: {
        type: Object,
        required: true,
        validator: item => item && item.label && item.value
      },
      button: {
        type: Object,
        default: null
      },
      disclaimer: {
        type: String,
        default: null
      }
    }
  }
</script>

<style lang="scss" scoped>
  .zg-how-we-helped {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: rem(24px);

    @include mq(medium) {
      flex-direction: row;
      gap: rem(38px);
    }

    .image {
      width: auto;
      height: rem(160px);

      @include mq(medium) {
        width: rem(320px);
        height: auto;
      }
    }

    .content {
      display: flex;
      flex-direction: column;
      gap: rem(24px);
    }

    h3 {
      margin: 0;

      @include mq(medium) {
        margin: 0;
      }
    }

    p {
      margin: 0;

      @include mq(medium) {
        margin: 0;
      }
    }

    .comparison {
      display: flex;
      flex-direction: row;

      @include mq(medium) {
        align-items: flex-end;
      }

      &__labels,
      &__before,
      &__after {
        display: flex;
        flex-direction: column;
        width: 50%;
        padding: rem(8px) 0;
        border-radius: radius(medium);

        @include mq(medium) {
          width: calc(100% / 3);
        }

        h4 {
          @include type-title-m;

          padding: rem(16px);
          margin: 0;
        }

        .values {
          display: flex;
          flex-direction: column;
          margin-top: auto;

          & > div {
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            flex-grow: 1;
            border-bottom: 1px solid color(gray-200);
            padding: rem(18px) rem(16px);
            margin: 0;

            @include mq(medium) {
              flex-direction: row;
              align-items: baseline;
              justify-content: flex-start;
            }

            &:last-child {
              border: none;
            }
          }
        }
      }

      &__labels {
        display: none;
        background-color: transparent;

        @include mq(medium) {
          display: flex;
        }

        span {
          @include type-title-s;

          margin: 0;
        }
      }

      &__before,
      &__after {
        span {
          &:first-child {
            @include type-title-xs;

            margin: 0 0 rem(18px);

            @include mq(medium) {
              display: none;
            }
          }

          &:last-child {
            @include type-body-m;

            margin: 0;
          }
        }

        .values {
          & > div:last-child {
            span:first-child {
              @include type-title-xs;

              margin: 0 0 rem(18px);
            }
          }
        }
      }

      &__before {
        background-color: transparent;
      }

      &__after {
        background-color: color(beige);
      }
    }

    .savings {
      display: flex;
      flex-direction: column;
      align-items: baseline;
      gap: rem(16px);

      @include mq(medium) {
        flex-direction: row;
      }

      span {
        &:first-child {
          @include type-title-m;

          margin: 0;
        }

        &:last-child {
          @include type-title-xl;

          margin: 0;
        }
      }
    }

    .button {
      border: none;
    }

    .disclaimer {
      @include type-body-s;

      display: flex;
      flex-direction: row;
      color: color(gray-500);
      margin: 0;

      span:first-child {
        margin-right: rem(2px);
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // modifiers
  // ? ----------------------------------------------------------------------
</style>
